/**
 * Who an internal final-round message goes to: the panel as DMs, or one channel.
 *
 * Shared by the pre-offer ask and the post-booking agenda, because TA asked for
 * the same choice in both places: the round template's own people are the
 * default, but a hiring manager or department lead often wants the FYI, so the
 * list stays editable — and sometimes a channel is simply the right audience.
 *
 * @finn.com only, enforced here AND on the proxy: these messages name a
 * candidate and their interview times, so they must not be addressable outside
 * the company.
 */
import { useState } from 'react';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { ChannelPicker } from './ChannelPicker';
import { PeopleField } from './PeopleField';
import { fullNameFromEmail } from './format';

export interface PanelAudience {
  /** DM recipients — used when `channel` is empty. */
  recipients: string[];
  /** "#name" or a C… id. Set means ONE channel post instead of DMs. */
  channel: string;
}

const isFinn = (e: string) => /^[^\s@]+@finn\.com$/.test(e.trim().toLowerCase());

/** Split a comma-separated field into valid FINN addresses. */
export function parseRecipients(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter((s) => isFinn(s))
    ),
  ];
}

export function PanelRecipientsField({
  id,
  /** The round template's people — the default audience. */
  suggested,
  value,
  onChange,
}: {
  id: string;
  suggested: string[];
  value: PanelAudience;
  onChange: (next: PanelAudience) => void;
}) {
  // Seeded from the panel, then whatever the recruiter types. Kept as TEXT while
  // editing so a half-typed address does not vanish on each keystroke.
  const [text, setText] = useState(() => (value.recipients.length ? value.recipients : suggested).join(', '));
  const [toChannel, setToChannel] = useState(!!value.channel);

  const parsed = parseRecipients(text);
  const invalid = text
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s && !isFinn(s));

  return (
    <div className="space-y-2">
      {!toChannel ? (
        <>
          {/* Searchable, like every other people field on the board — a
              hiring manager added to the FYI should not need their address
              typed out from memory. */}
          <PeopleField
            id={id}
            label="Send to"
            value={text}
            onChange={(next) => {
              setText(next);
              onChange({ recipients: parseRecipients(next), channel: '' });
            }}
          />
          <p className="text-xs text-gray-500">
            {parsed.length > 0
              ? `${parsed.map(fullNameFromEmail).join(', ')} — as individual Slack DMs.`
              : 'Nobody selected yet.'}
            {invalid.length > 0 && (
              <span className="text-[#791F1F]"> Ignoring: {invalid.join(', ')} (not @finn.com).</span>
            )}
          </p>
        </>
      ) : (
        <>
          <label className="block text-sm font-medium text-gray-700" htmlFor={`${id}-channel`}>
            Post to a Slack channel instead
          </label>
          <ChannelPicker
            id={`${id}-channel`}
            value={value.channel}
            onChange={(channel) => onChange({ recipients: [], channel })}
          />
        </>
      )}
      <Checkbox
        label="Post to a channel instead of DMs"
        labelClassName="text-sm text-gray-700"
        checked={toChannel}
        onCheckedChange={(v) => {
          const next = v === true;
          setToChannel(next);
          onChange(next ? { recipients: [], channel: '' } : { recipients: parsed, channel: '' });
        }}
      />
    </div>
  );
}

/** Is this audience usable — at least one DM recipient, or a channel? */
export const audienceReady = (a: PanelAudience): boolean =>
  a.channel.trim().length > 0 || a.recipients.length > 0;
